import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Compass, Sparkles } from "lucide-react";
import { PageHero, Section, Eyebrow, H2, P } from "@/components/SharedUI";

const OFFERINGS = [
  {
    icon: Compass,
    title: "Governance Archaeology Methodology™",
    desc:
      "A living research method that supports Nations to trace, remember and reconstruct their own systems of governance. Through story, kinship, Lore, Country, family line, ceremony, language and lived responsibility — what has been carried through, what has been interrupted, and what is being called back into place.",
    delivery:
      "Nation-owned research and reconstruction. Bigibila accompanies — never leads. Custodianship of the work stays with the Nation.",
  },
  {
    icon: ShieldCheck,
    title: "First Nations Data Sovereignty — operationalised",
    desc:
      "Where most conversations about IDS stop at principle, Bigibila has translated it into working practice. Authority maps, consent workflows, controlled access tiers, sovereign infrastructure, escalation pathways and audit evidence — designed so a Nation can hold its own data on its own terms, and see exactly who touched what, when, and under whose authority.",
    delivery:
      "Offered as a stewardship service, not an extraction system. Data belongs to the Nation. Bigibila protects and administers — not owns.",
  },
];

const RESEARCH_PROJECT = [
  ["Trace", "Family lines, kinship structures, language, ceremony and place — recorded and honoured as governance evidence, not folklore."],
  ["Remember", "Community-led remembering. What was known, who held it, who was interrupted from carrying it, and what came through anyway."],
  ["Reconstruct", "Nation-defined governance form. Bigibila offers structure and protection; the Nation decides what to rebuild and what to leave."],
  ["Protect", "Once reconstructed, the governance form is held under authority, permission, protection and evidence — the same architecture Bigibila offers government."],
];

const PRINCIPLES = [
  "Nations are already Sovereign. Bigibila does not confer sovereignty — it operationalises what is already there.",
  "Custodianship stays with community. Bigibila is a technical and governance steward, not an owner.",
  "The methodology is not a template. Every Nation's path is different; the method adapts to Country and Lore.",
  "Story is evidence. Lineage is evidence. Kinship is evidence. The method treats these with the same weight as any archive.",
  "Reconstruction is not restoration. It is a living response, not a museum piece.",
];

export default function Nations() {
  return (
    <>
      <PageHero
        eyebrow="For Nations"
        title={
          <>
            First Nations are Sovereign — <em className="not-italic text-[#9C4221]">always were, always will be.</em>
          </>
        }
        desc="Bigibila Nations is where sovereignty becomes system. Governance Archaeology to reconstruct what was carried through. Data Sovereignty to protect what is held now. A Nation-owned pathway, from Country to code."
      />

      {/* LORE ANCHOR */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase font-bold text-[#9C4221]">
                <Sparkles className="h-3 w-3" strokeWidth={2.5} /> Our Lore
              </div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight font-bold text-stone-900 leading-[1.1]">
                Bigibila Wiyayl is our Lore.
              </h2>
              <p className="mt-6 text-stone-800 leading-[1.85] max-w-lg">
                The story of Bigibila is not a metaphor. It is our operating Lore — the source that instructs how authority is held, how knowledge is protected, how responsibility is carried, and how consequence is met. Every product, every workflow and every architectural decision answers to it.
              </p>
              <p className="mt-4 text-stone-700 leading-relaxed max-w-lg italic">
                What has been carried through the matrilineal line — from Arthur Dodd through Sylvia Jessica Dodd, Barbara Helen Fernando and Liana Pitt — is the responsibility we sit under.
              </p>
              <div className="mt-8">
                <Link
                  to="/"
                  data-testid="nations-read-story"
                  className="inline-flex items-center gap-2 text-sm font-medium text-stone-900 border-b border-stone-300 pb-1 hover:border-stone-900 transition-colors"
                >
                  Read Bigibila Wiyayl <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-stone-200 bg-[#FAFAF9] p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#9C4221]/[0.05] blur-[80px]" />
                <div className="relative">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
                    A principle held in practice
                  </div>
                  <ul className="mt-6 space-y-4">
                    {PRINCIPLES.map((p) => (
                      <li key={p} className="flex gap-3 text-stone-800 leading-relaxed">
                        <span className="mt-2 h-1 w-1 bg-[#9C4221] rounded-full shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* WHAT WE OFFER NATIONS */}
      <section className="border-b border-stone-200 bg-stone-50">
        <Section>
          <div className="max-w-3xl mb-12">
            <Eyebrow>What we offer to Nations</Eyebrow>
            <H2 className="text-3xl md:text-4xl">Two staged offerings — one to remember, one to protect.</H2>
            <P className="mt-6">
              Both are Nation-owned. Both are optional. Both can be received in sequence or independently. Bigibila&apos;s role is to accompany, structure and hold — the direction, the pace and the decisions remain with the Nation.
            </P>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
            {OFFERINGS.map(({ icon: Icon, title, desc, delivery }) => (
              <article key={title} className="bg-[#FAFAF9] p-8 md:p-10" data-testid={`nations-offering-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="h-12 w-12 border border-stone-200 bg-stone-50 flex items-center justify-center mb-6">
                  <Icon className="h-6 w-6 text-[#9C4221]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl md:text-[28px] tracking-tight font-bold text-stone-900 leading-snug">
                  {title}
                </h3>
                <p className="mt-4 text-stone-700 leading-relaxed">{desc}</p>
                <div className="mt-6 border-l-2 border-[#9C4221]/40 pl-4">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-1">
                    How it is offered
                  </div>
                  <p className="text-sm text-stone-700 italic leading-relaxed">{delivery}</p>
                </div>
              </article>
            ))}
          </div>
        </Section>
      </section>

      {/* THE RESEARCH PROJECT */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <Eyebrow>The research project</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Governance Archaeology — remembering what has always been.</H2>
              <P className="mt-6">
                Governance did not begin with colonisation. Nations across this continent operated sophisticated relational systems — kinship, Lore, marriage systems, place, responsibility, obligation, accountability — for tens of thousands of years.
              </P>
              <P className="mt-4">
                Governance Archaeology is the disciplined work of tracing, remembering, and reconstructing those systems as they existed in a specific Nation, in a specific place, through a specific family line. It is research grounded in story, evidence, ceremony and lived responsibility — not imposed from a textbook.
              </P>
              <P className="mt-4">
                The work is slow, careful, and always led by the Nation.
              </P>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-px bg-stone-200 border border-stone-200">
                {RESEARCH_PROJECT.map(([stage, desc], i) => (
                  <div key={stage} className="bg-[#FAFAF9] p-6 grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-2">
                      <div className="font-display font-bold text-3xl tracking-tight text-stone-300">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <div className="md:col-span-10">
                      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">{stage}</div>
                      <p className="mt-2 text-stone-800 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* CONTACT PATH */}
      <section className="border-b border-stone-200 bg-stone-50">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>For your Nation</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Reach out on your own terms.</H2>
              <P className="mt-6">
                No form here. No procurement pathway. If your Nation is considering Governance Archaeology or data sovereignty stewardship, reach out directly. Kinship, quiet conversations and community authority come first.
              </P>
              <div className="mt-8 space-y-2">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">Direct — Nation-to-Nation</div>
                <a href="mailto:info@bigibilasystems.com" data-testid="nations-email-info" className="block font-display font-bold text-xl text-stone-900 hover:text-[#9C4221] transition-colors">
                  info@bigibilasystems.com
                </a>
                <a href="tel:+61466943229" data-testid="nations-phone" className="block text-sm text-stone-600 hover:text-[#9C4221] transition-colors">
                  0466 943 229
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-6 md:p-8">
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-2">Standing commitment</div>
                <p className="text-stone-800 leading-relaxed">
                  BIGIBILA SYSTEMS™ is not a database of culture. Nothing shared with us leaves the Nation without its authority. No cultural material is used to train external AI systems. Custodianship remains with community — always.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
